import { useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { useSendTest } from '@/hooks/use-send';
import { describeError } from '@/lib/describe-error';

/**
 * Sending yourself a test (spec § 4.4).
 *
 * Extracted from the send page so the WORKSPACE toolbar can open the same dialog, which is where the
 * spec actually asks for it: "send yourself a test with one click from the workspace toolbar". Two
 * entry points to one dialog rather than two dialogs — the second copy is where they drift.
 *
 * A test send goes through the SYSTEM email path, not the campaign pipeline. It cannot consume the
 * campaign's batch id, appear in its analytics, or be mistaken for a real send. Merge tags stay
 * visible as %%placeholders%% rather than being resolved, because resolving them would hide the
 * defect a test send is best at catching.
 */
/** A test to one address. Goes through the system-email path, so it never touches the campaign's numbers. */
export function TestSendDialog({
  campaignId,
  arms,
  onClose,
}: {
  campaignId: string;
  arms: { id: string; label: string }[];
  onClose: () => void;
}) {
  const [toEmail, setToEmail] = useState('');
  const [armId, setArmId] = useState(arms[0]?.id ?? '');
  const [touched, setTouched] = useState(false);

  const test = useSendTest(campaignId);

  const error = toEmail.trim() === '' ? 'Enter an address to send the test to.' : undefined;

  return (
    <Modal
      title="Send a test"
      description="Merge tags stay visible as %%placeholders%% rather than being filled in, so a tag that never gets a value is obvious."
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={test.isPending}>
            Close
          </Button>

          <Button type="submit" form="test-form" variant="primary" disabled={test.isPending}>
            {test.isPending ? 'Sending…' : 'Send test'}
          </Button>
        </>
      }
    >
      <form
        id="test-form"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          setTouched(true);

          if (error !== undefined) {
            document.getElementById('test-email')?.focus();
            return;
          }

          test.mutate({ armId, toEmail });
        }}
        className="flex flex-col gap-4"
      >
        {arms.length > 1 && (
          <div>
            <label htmlFor="test-arm" className="mb-1 block text-sm font-medium text-heading">
              Which arm?
            </label>

            <select
              id="test-arm"
              value={armId}
              onChange={(event) => setArmId(event.target.value)}
              className="h-10 w-full rounded-md border border-border bg-surface px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {arms.map((arm) => (
                <option key={arm.id} value={arm.id}>
                  {arm.label}
                </option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label htmlFor="test-email" className="mb-1 block text-sm font-medium text-heading">
            Send it to
          </label>

          <input
            id="test-email"
            type="email"
            value={toEmail}
            onChange={(event) => setToEmail(event.target.value)}
            onBlur={() => setTouched(true)}
            aria-invalid={touched && error !== undefined}
            aria-describedby={touched && error !== undefined ? 'test-email-error' : undefined}
            placeholder="you@hazcheck.com"
            className="h-10 w-full rounded-md border border-border bg-surface px-3 text-sm text-foreground placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-ring"
          />

          {touched && error !== undefined && (
            <p id="test-email-error" className="mt-1 text-xs text-danger-ink">
              {error}
            </p>
          )}
        </div>

        {test.isSuccess && (
          <p
            role="status"
            className="flex items-center gap-2 rounded-md bg-success-bg px-3 py-2 text-sm text-success-ink"
          >
            <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />
            Sent. Check your inbox — and read the raw source to see the unsubscribe headers.
          </p>
        )}

        {test.isError && (
          <p role="alert" className="rounded-md bg-danger-bg px-3 py-2 text-sm text-danger-ink">
            {describeError(test.error).message}
          </p>
        )}
      </form>
    </Modal>
  );
}
